import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { evFacetClick, evGroupedPillToggle } from 'events/search';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import { FILTERS_RE, NOFOLLOW_FACET_RE } from 'common/regex';
import { trackEvent } from 'helpers/analytics';
import { track } from 'apis/amethyst';

import styles from 'styles/components/search/dropdownPills.scss';

export class DropdownPills extends Component {
  static displayName = 'DropdownPills';

  state = {
    visibleDropdown: {}
  };

  componentDidUpdate(prevProps, { visibleDropdown: oldDropdown }) {
    const { visibleDropdown } = this.state;
    if (visibleDropdown?.facetField && visibleDropdown?.keyDown && visibleDropdown.facetField !== oldDropdown?.facetField) {
      this.pillValuesWrapper.querySelector('button').focus();
    }
  }

  makeBreadcrumbClickHandler = crumb => {
    const { router } = this.context;
    const { removePersonalizedSize, filters } = this.props;
    return e => {
      e.preventDefault();
      if (filters?.personalizedSize?.sizes?.includes(crumb.name)) {
        removePersonalizedSize();
      }
      router.forceBrowserPush(crumb.removeUrl);
      track(() => ([
        evFacetClick, {
          breadcrumbRemove: crumb,
          facetClickSource: 'FACET_PILLS',
          selected: false,
          deselected: true
        }
      ]));
    };
  };

  makeCrumbContent = (crumb, breadcrumb) => {
    const { testId } = this.context;
    const crumbTitle = `Remove ${crumb} filter`;

    return (
      <a // eslint-disable-line jsx-a11y/anchor-is-valid
        href={!FILTERS_RE.test(breadcrumb.removeUrl) ? breadcrumb.removeUrl : ''}
        aria-label={crumbTitle}
        data-test-id={testId('groupedBreadcrumb')}
        onClick={this.makeBreadcrumbClickHandler(breadcrumb)}>
        {crumb}
      </a>
    );
  };

  onPillClick = (e, crumb, index) => {
    const { onFacetSelect } = this.props;
    const { visibleDropdown: { facetField, groupIndex } } = this.state;
    onFacetSelect(facetField, crumb.name, groupIndex, index, false, 'zEverythingElse');
    trackEvent('TE_SEARCH_PILLCLICKED', `${crumb.name}:${crumb.value}`);
    track(() => ([
      evFacetClick, { crumb, facetClickSource: 'FACET_PILLS', selected: true, deselected: false }
    ]));

    e.preventDefault();
  };

  closePillGroup = () => {
    this.setState({ visibleDropdown: {} });
  };

  onPillGroupClick(group, groupIndex, keyDown = false) {
    const { visibleDropdown } = this.state;
    const isOpen = group.facetField !== visibleDropdown?.facetField;
    this.setState({ visibleDropdown: isOpen ? { ...group, groupIndex, keyDown } : this.closePillGroup() });
    track(() => ([
      evGroupedPillToggle, {
        facetGroup: group.facetField,
        facetClickSource: 'FACET_PILLS_DROPDOWN',
        open: isOpen,
        collapse: !isOpen
      }
    ]));
  }

  onPillGroupKeyDown(e, group, groupIndex) {
    if ([' ', 'Enter'].includes(e.key)) {
      e.preventDefault();
      this.onPillGroupClick(group, groupIndex, true);
    }
  }

  makeDropdownSelections = () => {
    const { visibleDropdown } = this.state;
    const chosenGroup = visibleDropdown?.values?.length;
    const groupDisplayname = visibleDropdown?.facetFieldDisplayName;

    let possibleValues = [];
    if (chosenGroup) {
      possibleValues = visibleDropdown.values.map((v, i) => {
        const relType = NOFOLLOW_FACET_RE.test(v.facetField) ? 'nofollow' : undefined;
        return (
          <li key={`${v.name}`} className={cn(styles.pillValue, { [styles.selected]: v.isSelected })}>
            <a
              rel={v.facetUrl ? relType : undefined}
              tabIndex={0}
              aria-label={`${v.isSelected ? 'Disable' : 'Enable'} filter for ${v.displayName} in ${groupDisplayname}, ${v.count} products available`}
              href={v.facetUrl}
              onKeyPress={e => this.onPillClick(e, v, i)}
              onClick={e => this.onPillClick(e, v, i)}>
              {v.displayName}{v.count > 0 && ` (${v.count})`}
            </a>
          </li>
        );
      });
    }

    return (
      <div className={cn(styles.wrapper, { [styles.expanded]: chosenGroup })} ref={el => this.pillValuesWrapper = el}>
        {chosenGroup && !!possibleValues.length && (
          <>
            <div>
              <h3>{groupDisplayname}</h3>
              <button type="button" className={styles.close} onClick={this.closePillGroup}>close {groupDisplayname}</button>
            </div>
            <div className={styles.valueWrapper}>
              <ul>
                {possibleValues}
              </ul>
            </div>
          </>
        )}
      </div>
    );
  };

  makePillMarkup() {
    const { facets, filters } = this.props;
    const { visibleDropdown } = this.state;
    const chosenBreadcrumbs = [ ...filters.breadcrumbs ];

    const toggledGroup = visibleDropdown?.facetField;

    // loop over all facets
    const groupedPills = facets.navigation?.zEverythingElse.reduce((arr, facetGroup, i) => {
      const { facetField, facetFieldDisplayName } = facetGroup;
      // check if there's a breadcrumb
      const selectedValues = facetGroup.values.reduce((valueArr, value) => {
        const { displayName } = value;
        const breadcrumbIndex = filters.breadcrumbs.findIndex(b => {
          // ensure that the value and facetGroup match
          // since same values can live in different facetGroups
          const valueIsBreadcrumb = b.name === displayName && b.removeName.includes(facetField);
          if (valueIsBreadcrumb) {
            // remove it from breadcrumbs if exists
            const chosenIndex = chosenBreadcrumbs.findIndex(c => c.name === displayName && c.removeName.includes(facetField));
            if (chosenIndex > -1) {
              chosenBreadcrumbs.splice(chosenIndex, 1);
            }
          }
          return valueIsBreadcrumb;
        });

        if (breadcrumbIndex > -1) {
          valueArr.push(
            <li key={`${displayName}`}>
              {this.makeCrumbContent(displayName, filters.breadcrumbs[breadcrumbIndex])}
            </li>
          );
        }
        return valueArr;
      }, []);

      arr.push(
        <li key={`${facetField}-${facetField}`} className={cn(styles.groupedPill, { [styles.hasSelections]: !!selectedValues.length })}>
          <button
            type="button"
            key={i}
            className={cn({ [styles.isToggled]: facetField === toggledGroup })}
            aria-expanded={facetField === toggledGroup}
            onClick={() => this.onPillGroupClick(facetGroup, i)}
            onKeyDown={e => this.onPillGroupKeyDown(e, facetGroup, i)}
            aria-label={`Select ${facetFieldDisplayName} filter`}>
            {facetFieldDisplayName}{!!selectedValues.length && ` (${selectedValues.length})`}
          </button>
          {!!selectedValues.length && (
            <ul>{selectedValues}</ul>
          )}
        </li>);
      return arr;
    }, []);

    const remainingBreadcrumbs = chosenBreadcrumbs.map(bcrumb => {
      const { removeUrl, name } = bcrumb;
      return (
        <li className={styles.breadCrumb} key={`${removeUrl}-${name}`}>
          {this.makeCrumbContent(name, bcrumb)}
        </li>
      );
    });

    return remainingBreadcrumbs.concat(groupedPills);
  }

  handleBreadcrumbClick(event) {
    const { onV2CrumbSelect } = this.props;
    onV2CrumbSelect(event);
    event.preventDefault();
    return false;
  }

  render() {
    const { facets, filters, className } = this.props;
    const { testId } = this.context;
    if (filters?.breadcrumbs?.length || facets.navigation?.zEverythingElse?.length) {
      return (
        <div className={cn(styles.dropDownPills, className || null)} data-test-id={testId('breadcrumbScroll')}>
          <ul className={styles.pillGroups} ref={e => this.breadcrumbWrapper = e}>
            {this.makePillMarkup()}
          </ul>
          {this.makeDropdownSelections() }
        </div>
      );
    }
    return null;
  }
}

DropdownPills.contextTypes = {
  testId: PropTypes.func,
  router: PropTypes.object.isRequired
};

export default withErrorBoundary('DropdownPills', DropdownPills);
